import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { createCaseInDatabase, saveDraftToDatabase } from '../useClaimState';
import type { ClaimState, PhaseId } from '../types';

interface UseCaseManagerParams {
  state: ClaimState;
  setCaseId: (caseId: string, caseNumber: string) => void;
}

interface UseCaseManagerReturn {
  isCreatingCase: React.MutableRefObject<boolean>;
}

/**
 * Case manager hook for new claim flow.
 * Handles automatic case creation when transitioning past booking_review.
 */
export function useCaseManager({ state, setCaseId }: UseCaseManagerParams): UseCaseManagerReturn {
  const isCreatingCase = useRef(false);

  // Create case after booking_review when transitioning to incident phase
  useEffect(() => {
    const createCase = async () => {
      // Create case when moving past booking_review and no case exists yet
      const shouldCreateCase =
        !state.caseId &&
        !isCreatingCase.current &&
        state.editableSegments.length > 0 &&
        (state.phase === 'incident_intro' ||
          state.phase === 'incident_select' ||
          state.phase === 'incident_details');

      if (shouldCreateCase) {
        isCreatingCase.current = true;
        try {
          const { caseId, caseNumber } = await createCaseInDatabase();
          setCaseId(caseId, caseNumber);

          // Save initial state to database
          await saveDraftToDatabase(caseId, state);
          toast.success('Claim saved');
        } catch (err) {
          console.error('Failed to create case:', err);
          toast.error('Failed to save claim. Please try again.');
        } finally {
          isCreatingCase.current = false;
        }
      }
    };
    createCase();
  }, [state.phase, state.caseId, state.editableSegments.length, setCaseId, state]);

  return {
    isCreatingCase,
  };
}
